import { useEffect, useRef } from "react";
import websocketService from "../../../services/websocket";
import { useMonthCalendarReservations } from "../../../hooks/useReservations";
import { useQueryClient } from "@tanstack/react-query";
import { logger } from "../../../utils/logger";
import {
  beginReservationInvalidation,
  endReservationInvalidation,
  invalidateReservationQueries,
  removeReservationFromMonthCalendarCache,
  upsertReservationInMonthCalendarCache,
} from "../../../utils/queryInvalidation";

const getReservaSocketId = (data: {
  id?: number;
  reserva_id?: number;
  reserva_data?: { id?: number };
}) => data.reserva_id ?? data.id ?? data.reserva_data?.id;

export const useMonthReservations = (selectedDate: Date) => {
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // 🔥 OPTIMIZACIÓN: Extraer año y mes de la fecha seleccionada
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth() + 1; // getMonth() devuelve 0-11, necesitamos 1-12

  // 🚀 Usar React Query para obtener reservas del mes específico
  const {
    data: calendarReservationsData,
    isLoading,
    isError,
    error,
    refetch: reloadReservations,
  } = useMonthCalendarReservations(year, month);

  const reservations = calendarReservationsData?.reservas || [];

  // 🚀 Configurar WebSocket para actualizaciones en tiempo real
  useEffect(() => {
    unsubscribeRef.current = websocketService.onReservaChange((type, data) => {
      logger.debug(`📅 Calendar WebSocket Event: ${type}`, data);

      const lastLocalInvalidation = queryClient.getQueryData([
        "last-local-invalidation",
      ]) as number | undefined;
      const now = Date.now();

      if (
        lastLocalInvalidation &&
        now - lastLocalInvalidation < 5000 &&
        type !== "created"
      ) {
        logger.debug(
          `📅 Calendar WebSocket: Skipping invalidation (local invalidation ${
            now - lastLocalInvalidation
          }ms ago) - Event: ${type}`
        );
        return;
      }

      const isInvalidating = queryClient.getQueryData([
        "is-invalidating",
      ]) as boolean;
      if (isInvalidating) {
        logger.debug(
          "📅 Calendar WebSocket: Skipping invalidation (already invalidating)"
        );
        return;
      }

      if (type === "deleted") {
        const reservaId = getReservaSocketId(data);
        if (reservaId) {
          const cacheUpdated = removeReservationFromMonthCalendarCache(
            queryClient,
            reservaId
          );
          queryClient.removeQueries({
            queryKey: ["reservation", reservaId],
          });
          if (cacheUpdated) return;
        }
      }

      const socketReservation = data.reserva_data;
      if (
        socketReservation?.id &&
        socketReservation.entrada &&
        (type === "created" || type === "updated")
      ) {
        const cacheUpdated = upsertReservationInMonthCalendarCache(
          queryClient,
          socketReservation
        );
        queryClient.setQueryData(
          ["reservation", socketReservation.id],
          socketReservation
        );
        if (cacheUpdated) return;
      }

      beginReservationInvalidation(queryClient);
      invalidateReservationQueries(
        queryClient,
        type === "created" ? data.reserva_data : undefined
      );

      setTimeout(() => {
        endReservationInvalidation(queryClient);
      }, 1000);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [queryClient]);

  return {
    reservations,
    reloadReservations,
    isLoading,
    isError,
    error,
  };
};
